import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import Footer from '../../components/Footer';
import { Header } from '../../components/Header';
import Seo from '../../components/Seo';

const ease = [0.16, 1, 0.3, 1];

const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-6">
    <ol className="flex items-center gap-1.5 text-[11px] text-[var(--text-3)]">
      {items.map((item, i) => (
        <li key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3 h-3" />}
          {item.href ? (
            <Link to={item.href} className="hover:text-[var(--accent)] transition-colors">{item.label}</Link>
          ) : (
            <span className="text-[var(--text-2)]">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export default function FeaturePage({
  seo,
  eyebrow,
  title,
  titleAccent,
  subtitle,
  heroContent,
  sections,
  faqItems,
  ctaTitle,
  ctaText,
  breadcrumbLabel,
}) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.title,
    description: seo.description,
    url: `https://www.myfootballgame.com${seo.path}`,
    isPartOf: { '@type': 'WebSite', name: 'My Football Game', url: 'https://www.myfootballgame.com' },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.myfootballgame.com/' },
        { '@type': 'ListItem', position: 2, name: breadcrumbLabel || seo.title, item: `https://www.myfootballgame.com${seo.path}` },
      ],
    },
  };

  const faqJsonLd = faqItems?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  } : null;

  return (
    <motion.div
      className="w-full min-h-screen overflow-hidden"
      style={{ background: 'var(--surface-0)', color: 'var(--text-1)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <Seo title={seo.title} description={seo.description} path={seo.path} lang={seo.lang} jsonLd={faqJsonLd || jsonLd} />

      <div className="container mx-auto px-4">
        <Header />
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        <Breadcrumbs items={[
          { label: 'Home', href: '/' },
          { label: breadcrumbLabel || title },
        ]} />

        {/* Hero */}
        <motion.div
          className="text-center mb-16"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5, ease }}
        >
          <div className="pc-eyebrow pc-eyebrow-accent mb-2">{eyebrow}</div>
          <h1 className="pc-display text-4xl md:text-6xl mb-4">
            {title}<span style={{ color: 'var(--accent)' }}>{titleAccent}</span>
          </h1>
          <p className="text-base text-[var(--text-2)] max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
        </motion.div>

        {heroContent}

        {/* Sections */}
        {sections?.map((section, i) => (
          <motion.section
            key={i}
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              {section.icon && (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)' }}>
                  {section.icon}
                </div>
              )}
              <h2 className="text-2xl font-bold">{section.title}</h2>
            </div>
            <div className="text-[var(--text-2)] leading-relaxed space-y-4">
              {section.content}
            </div>
          </motion.section>
        ))}

        {/* FAQ */}
        {faqItems?.length > 0 && (
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqItems.map((item, i) => (
                <details key={i} className="rounded-lg overflow-hidden group" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}>
                  <summary className="px-5 py-4 cursor-pointer font-bold text-sm hover:text-[var(--accent)] transition-colors list-none flex items-center justify-between">
                    {item.q}
                    <ChevronRight className="w-4 h-4 text-[var(--text-3)] transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-4 text-sm text-[var(--text-2)] leading-relaxed" style={{ borderTop: '1px solid var(--border-edge)' }}>
                    <div className="pt-3">{item.a}</div>
                  </div>
                </details>
              ))}
            </div>
          </motion.section>
        )}

        {/* Internal Links */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-lg font-bold mb-4">Explore More</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: '/how-to-play', label: 'How to Play' },
              { to: '/leaderboard', label: 'Leaderboard' },
              { to: '/blog', label: 'Manager Guides' },
              { to: '/faq', label: 'FAQ' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="rounded-lg px-4 py-3 text-xs font-bold text-center transition-all hover:text-[var(--accent)]"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)', color: 'var(--text-2)' }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          className="rounded-xl p-8 md:p-12 text-center relative overflow-hidden"
          style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, var(--accent), transparent 60%)' }} />
          <h2 className="text-2xl md:text-3xl font-bold mb-3 relative z-10">{ctaTitle || 'Ready to manage?'}</h2>
          <p className="text-sm text-[var(--text-2)] mb-6 relative z-10 max-w-lg mx-auto">{ctaText || 'Create your club for free and start playing today. No download needed.'}</p>
          <Link to="/register" className="relative z-10">
            <motion.button
              className="px-7 py-3 rounded font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2 transition-all"
              style={{ background: 'var(--accent)', color: 'var(--surface-0)', boxShadow: '0 0 24px var(--accent-glow)' }}
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              Play Free Now <ArrowRight className="w-4 h-4" />
            </motion.button>
          </Link>
        </motion.div>
      </div>

      <Footer />
    </motion.div>
  );
}
